import styled from 'styled-components';
import { FlexRowMiddle } from './Flex';
import { IconButton } from './Icon';
import { Text } from './Text';
import { MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import IconEdit from '../../../public/icons/edit.svg?react';
import IconClose from '../../../public/icons/close.svg?react';

const StyledEditCell = styled(FlexRowMiddle)`
	max-width: 75%;
	width: 100%;
	height: 100%;
	overflow: hidden;
`;

export function EditCellBase<T>({
	children,
	value,
	saveValues,
	disabled,
	renderEditor,
	minWidth,
	altColor,
}: {
	altColor?: boolean;
	minWidth?: string;
	children: ReactNode;
	value: T;
	saveValues: (select: T) => Promise<void>;
	disabled?: boolean;
	renderEditor: (props: { value: T; onChange: (value: T) => void; saving: boolean }) => ReactNode;
}) {
	const [unsavedValue, setUnsavedValue] = useState<T>(value);
	const [savingValue, setSavingValue] = useState<boolean>(false);
	const [editingActive, setEditingActive] = useState<boolean>(false);

	const editingRef = useRef<boolean>(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const onClickEdit = () => {
		setEditingActive(true);
		editingRef.current = true;
		setUnsavedValue(value);
	};

	const onClickCancel = (e: MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		setEditingActive(false);
		editingRef.current = false;
	};

	const onBlur = useCallback(async () => {
		if (!editingRef.current) return;
		editingRef.current = false;
		setSavingValue(true);
		await saveValues(unsavedValue);
		setEditingActive(false);
		setSavingValue(false);
	}, [saveValues, unsavedValue]);

	useEffect(() => {
		const handleClickOutside = (event: globalThis.MouseEvent) => {
			if (editingRef.current && containerRef.current && !containerRef.current.contains(event.target! as Node)) {
				onBlur();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [containerRef, onBlur]);

	// TODOL Change the text to a div because content doesnt have to be text
	return (
		<StyledEditCell ref={containerRef} style={{ minWidth: `calc(${minWidth ?? '150px'} + 24px)` }}>
			{!editingActive && (
				<>
					<Text $color={altColor ? 'text_desc_alt' : 'text_desc_main'} $size="1.2rem" $weight="400">
						{children}
					</Text>
					<IconButton
						$colorFill="text_desc_main"
						$colorFillHover="text_theme"
						onClick={() => onClickEdit()}
						style={{ marginLeft: '1rem', height: 14, cursor: 'pointer' }}
						disabled={disabled}
					>
						<IconEdit />
					</IconButton>
				</>
			)}
			{editingActive && (
				<>
					{renderEditor({
						value: unsavedValue,
						onChange: setUnsavedValue,
						saving: savingValue,
					})}
					<IconButton
						$colorFill="text_desc_main"
						$colorFillHover="text_theme"
						onClick={onClickCancel}
						disabled={savingValue || disabled}
						style={{ marginLeft: '1rem', height: 14, cursor: 'pointer' }}
					>
						<IconClose />
					</IconButton>
				</>
			)}
		</StyledEditCell>
	);
}
